import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { translatedMessage } from '../../service/LanguageService';
import DataTableUtils from '../../utilities/DataTableUtils';
import DeleteModalComponent from '../DeleteModalComponent';
import SubjectEditDialog from './SubjectEditDialog';
import { CompetitionService } from '../../service/CompetitionService';
import ExtractSubjectsDialog from './ExtractSubjectsDialog';
import ApiService from '../../service/ApiService';
import ApiUtils from '../../utilities/ApiUtils';
import InvalidateExtractedSubjectsDialog from './InvalidateExtractedSubjectsDialog';
import ChangeSubjectsStructureDialog from './ChangeSubjectsStructureDialog';
import { Message } from 'primereact/message';
import SubjectViewDialog from './SubjectViewDialog';

const SubjectListComponent = (props) => {
    const [subjects, setSubjects] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [extractSubjectsDialogVisible, setExtractSubjectsDialogVisible] = useState(false);
    const [invalidateSubjectsDialogVisible, setInvalidateSubjectsDialogVisible] = useState(false);
    const [changeSubjectsStructureDialogVisible, setChangeSubjectsStructureDialogVisible] = useState(false);
    const [viewSubjectDialogVisible, setViewSubjectDialogVisible] = useState(false);

    const [competitionBibliography, setCompetitionBibliography] = useState([]);

    const toast = useRef(null);
    const dt = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);

    useEffect(() => {
        setIsLoading(true);

        setSubjects(props.subjects);

        getCompetitionBibliography().then((r) => setIsLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.subjects, competitionService]);

    const getCompetitionBibliography = async () => {
        competitionService
            .getCompetitionBibliography(props.competition?.id)
            .then((_competitionBibliography) => {
                setCompetitionBibliography(_competitionBibliography);
                setIsLoading(false);
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const getSubjects = async () => {
        competitionService
            .getCompetitionSubjects(props.competition?.id)
            .then((_subjects) => {
                setSubjects(_subjects);
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const existProposals = () => {
        return subjects.some((subject) => subject.proposals.length > 0);
    };

    const getSubjectsErrors = () => {
        let errors = [];

        let usedCompetitionBibliographyIds = subjects.flatMap((subject) => subject.bibliography.flatMap((subjBibliography) => subjBibliography?.competitionBibliography?.id));
        let competitionBibliographyIds = competitionBibliography.map((bibliography) => bibliography.id);
        const allCompBibliographiesAreUsed = competitionBibliographyIds.every((num) => usedCompetitionBibliographyIds.includes(num));
        if (!allCompBibliographiesAreUsed) {
            errors.push(translatedMessage('competition.subjects.notAllBibliographyUsed'));
        }

        const totalSubjectsScore = subjects.reduce((sum, subject) => sum + subject.score, 0);
        if (totalSubjectsScore !== 100) {
            errors.push(translatedMessage('competition.subjects.scoreNotEqual100'));
        }

        return errors;
    };

    const updateList = async (savedSubject, isNew) => {
        if (typeof props.afterSave === 'function') {
            props.afterSave();
        } else {
            await getSubjects();
        }
    };

    const showAddSubjectDialog = () => {
        setSelectedSubject({ name: translatedMessage('competition.subject') + ' ' + (subjects.length + 1), score: '', bibliography: [], proposals: [] });
        setEditDialogVisible(true);
    };

    const showEditDialog = (subject) => {
        setSelectedSubject(subject);
        setEditDialogVisible(true);
    };

    const showDeleteDialog = (subject) => {
        setSelectedSubject(subject);
        setDeleteDialogVisible(true);
    };

    const hideDeleteDialog = () => {
        setDeleteDialogVisible(false);
    };

    const showChangeSubjectsStructureDialog = () => {
        setChangeSubjectsStructureDialogVisible(true);
    };

    const showExtractSubjectDialog = () => {
        setExtractSubjectsDialogVisible(true);
    };

    const showInvalidateSubjectsDialog = () => {
        setInvalidateSubjectsDialogVisible(true);
    };

    const deleteSubject = () => {
        competitionService
            .deleteSubject(selectedSubject.id)
            .then(() => {
                let _subjects = subjects.filter((item) => item.id !== selectedSubject.id);
                setSubjects(_subjects);
                if (typeof props.afterSave === 'function') {
                    props.afterSave(selectedSubject, false);
                }
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.delete.success') });
            })
            .catch((error) => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            })
            .finally(() => {
                setDeleteDialogVisible(false);
                setSelectedSubject(null);
            });
    };

    const downloadProposals = async (subject) => {
        await downloadDocument('/competition/' + props.competition?.id + '/proposalsPdf', 'propuneri_subiecte_' + props.competition?.id + '.pdf');
    };

    const downloadExtractedSubject = async (subject) => {
        await downloadDocument('/competition/' + props.competition?.id + '/extractedSubjectPdf', 'subiect_extrase_' + props.competition?.id + '.pdf');
    };

    const downloadDocument = async (downloadURL, defaultFileName) => {
        try {
            const response = await ApiService.getAuthenticatedInstance().get(downloadURL, { responseType: 'blob' });
            const contentDisposition = response.headers['content-disposition'];
            let filename = defaultFileName;
            if (contentDisposition) {
                // Regular expression to extract filename (handles quoted and unquoted filenames)
                const filenameMatch = contentDisposition.match(/filename\*?=["']?([^;"]+)["']?/i);
                if (filenameMatch && filenameMatch[1]) {
                    // Decode URI component for potential UTF-8 encoded filenames
                    filename = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ''));
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            link.click();
            setTimeout(() => window.URL.revokeObjectURL(url), 0);
        } catch (error) {
            let errorDetails = await error?.response?.data?.text();
            let errorMessage = ApiUtils.computeFileDownloadGetError(errorDetails, 'error.file.not_found');
            errorMessage && toast?.current?.show({ severity: 'error', summary: translatedMessage(errorMessage), life: 5000 });
        }
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                {existProposals() && props.permissions.canEditSubjects && (
                    <div className="my-2">
                        <Button label={translatedMessage('competition.subjects.changeStructure')} icon="pi pi-plus" className="p-button-primary mr-2" onClick={showChangeSubjectsStructureDialog} />
                    </div>
                )}
                {!existProposals() && props.permissions.canEditSubjects && (
                    <div className="my-2">
                        <Button label={translatedMessage('competition.subjects.add')} icon="pi pi-plus" className="p-button-primary mr-2" onClick={showAddSubjectDialog} />
                    </div>
                )}
                {existProposals() && (
                    <div className="my-2">
                        <Button label={translatedMessage('competition.subjectProposals.pdf')} icon="pi pi-download" className="p-button-primary mr-2" onClick={downloadProposals} />
                    </div>
                )}
                {existProposals() && props.permissions.canExtractSubjects && (
                    <div className="my-2">
                        <Button label={translatedMessage('competition.subjectProposals.extract')} icon="pi pi-plus" className="p-button-primary mr-2" onClick={showExtractSubjectDialog} />
                    </div>
                )}
                {props.permissions.canInvalidateExtractedSubjects && (
                    <>
                        <div className="my-2">
                            <Button label={translatedMessage('competition.extractedSubjects.pdf')} icon="pi pi-download" className="p-button-primary mr-2" onClick={downloadExtractedSubject} />
                        </div>
                        <div className="my-2">
                            <Button label={translatedMessage('competition.extractedSubjects.invalidate')} icon="pi pi-plus" className="p-button-primary mr-2" onClick={showInvalidateSubjectsDialog} />
                        </div>
                    </>
                )}
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {
        //return <ExportButtonComponent getExportData={handleExport} header={tableHeader} fileName={GeneralUtils.replaceSpaces(translatedMessage('competition.subjects'))} />;
    };

    const bibliographyTemplate = (rowData) => {
        return (
            <>
                {rowData.bibliography.map((item) => (
                    <div className="grid" key={item?.id}>
                        <div className="col-4" key={item?.id + '_1'}>
                            {item?.competitionBibliography?.bibliography}
                        </div>
                        <div className="col-8" key={item?.id + '_2'}>
                            {item?.competitionBibliography?.topic}
                        </div>
                    </div>
                ))}
            </>
        );
    };

    function showViewSubjectDialog(rowData) {
        setSelectedSubject(rowData);
        setViewSubjectDialogVisible(true);
    }

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                <Button icon="pi pi-eye" className="p-button-rounded p-button-primary m-1" onClick={() => showViewSubjectDialog(rowData)} tooltip={translatedMessage('generic.view')} tooltipOptions={{ showOnDisabled: true, position: 'top' }} />
                {props.permissions.canEditSubjects && (
                    <Button icon="pi pi-pencil" className="p-button-rounded p-button-info m-1" onClick={() => showEditDialog(rowData)} tooltip={translatedMessage('generic.edit')} tooltipOptions={{ showOnDisabled: true, position: 'top' }} />
                )}
                {props.permissions.canEditSubjects && (
                    <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-danger m-1"
                        onClick={() => showDeleteDialog(rowData)}
                        disabled={rowData.id < 1000}
                        tooltip={translatedMessage('generic.delete')}
                        tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                    />
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div>
                    <h5 className="mb-1 font-weight-normal">{translatedMessage('competition.subjects')}</h5>
                </div>
                <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="px-0 pt-0"></Toolbar>

                {props.permissions.canEditSubjects &&
                    getSubjectsErrors().map((error, index) => (
                        <div key={index}>
                            <Message className="mb-1" severity="error" text={error} />
                        </div>
                    ))}

                <DataTable
                    ref={dt}
                    value={subjects}
                    dataKey="id"
                    paginator
                    rows={DataTableUtils.defalRowsPerPage()}
                    rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                    className="datatable-responsive pcn-datatable"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={'{first} - {last} ' + translatedMessage('generic.of') + ' {totalRecords}'}
                    emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                    responsiveLayout="scroll"
                    // filterDisplay="row"
                    // filters={filters}
                    // globalFilterFields={['name', 'type', 'active']}
                >
                    <Column field="name" header={translatedMessage('generic.nameRo')} sortable headerStyle={{ width: '10%', minWidth: '8rem' }} />
                    <Column field="score" header={translatedMessage('subject.score')} sortable headerStyle={{ width: '5%', minWidth: '3rem' }} />
                    <Column field="bibliography" body={bibliographyTemplate} header={translatedMessage('subject.bibliography')} sortable headerStyle={{ width: '75%', minWidth: '10rem' }} />
                    <Column field="proposals.length" header={translatedMessage('subject.proposalsNo')} sortable headerStyle={{ width: '5%', minWidth: '5rem' }} />
                    <Column body={actionBodyTemplate} bodyStyle={{ textAlign: 'right' }} />
                </DataTable>

                {editDialogVisible && (
                    <SubjectEditDialog
                        value={selectedSubject}
                        visible={editDialogVisible}
                        visibleSetter={setEditDialogVisible}
                        afterSave={updateList}
                        competition={props.competition}
                        competitionBibliography={competitionBibliography}
                        permissions={props.permissions}
                    />
                )}

                {deleteDialogVisible && <DeleteModalComponent visible={deleteDialogVisible} item={selectedSubject ? selectedSubject.name : ''} closeDialog={hideDeleteDialog} deleteRecord={deleteSubject} />}

                {extractSubjectsDialogVisible && <ExtractSubjectsDialog visible={extractSubjectsDialogVisible} visibleSetter={setExtractSubjectsDialogVisible} competition={props.competition} translate={true} afterSave={props.afterSave} />}

                {invalidateSubjectsDialogVisible && <InvalidateExtractedSubjectsDialog visible={invalidateSubjectsDialogVisible} visibleSetter={setInvalidateSubjectsDialogVisible} competition={props.competition} afterSave={props.afterSave} />}

                {changeSubjectsStructureDialogVisible && (
                    <ChangeSubjectsStructureDialog visible={changeSubjectsStructureDialogVisible} visibleSetter={setChangeSubjectsStructureDialogVisible} competition={props.competition} translate={true} afterSave={props.afterSave} />
                )}

                {viewSubjectDialogVisible && <SubjectViewDialog value={selectedSubject} visible={viewSubjectDialogVisible} visibleSetter={setViewSubjectDialogVisible} />}
            </>
        );
    }
};

export default SubjectListComponent;
